import React, { useState, useEffect } from 'react';
import {
  AiOutlineLike,
  AiFillLike,
  AiOutlineDislike,
  AiFillDislike
} from 'react-icons/ai';
import { updateBillVote, getBillVote } from '../../../utils/utils';
import './LikeDislikeButtons.scss';
import AuthRequiredModal from '../../AlertComponents/AuthRequiredModal';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../context/UserContext';

const LikeDislikeButtons = ({ billId, verifiedOnly = false }) => {
  const navigate = useNavigate();
  const { dnaUserId } = useUser() || {};
  const [vote, setVote] = useState(null);
  const [likeCount, setLikeCount] = useState(0);
  const [dislikeCount, setDislikeCount] = useState(0);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  useEffect(() => {
    const fetchVote = async () => {
      try {
        const data = await getBillVote(billId, { verifiedOnly });

        if (data.vote === true) {
          setVote('like');
        } else if (data.vote === false) {
          setVote('dislike');
        } else {
          setVote(null);
        }

        setLikeCount(data.like_count);
        setDislikeCount(data.dislike_count);
      } catch (err) {
        console.error('Failed to load vote and counts:', err);
      }
    };

    fetchVote();
  }, [billId, verifiedOnly]);

  const refreshVoteData = async () => {
    try {
      const data = await getBillVote(billId, { verifiedOnly });
      if (data.vote === true) {
        setVote('like');
      } else if (data.vote === false) {
        setVote('dislike');
      } else {
        setVote(null);
      }

      setLikeCount(data.like_count);
      setDislikeCount(data.dislike_count);
    } catch (err) {
      console.error("Error refreshing vote data:", err);
    }
  };

  const handleLike = async () => {
    if (!dnaUserId) {
      setAuthModalOpen(true);
      return;
    }
    try {
      if (vote === 'like') {
        await updateBillVote(billId, null);
        setVote(null);
      } else {
        await updateBillVote(billId, true);
        setVote('like');
      }
      await refreshVoteData();
    } catch (err) {
      console.error(`Error updating like: ${err.message}`);
    }
  };

  const handleDislike = async () => {
    if (!dnaUserId) {
      setAuthModalOpen(true);
      return;
    }
    try {
      if (vote === 'dislike') {
        await updateBillVote(billId, null);
        setVote(null);
      } else {
        await updateBillVote(billId, false);
        setVote('dislike');
      }
      await refreshVoteData();
    } catch (err) {
      console.error(`Error updating dislike: ${err.message}`);
    }
  };

  const viewComments = () => {
    const commentsSection = document.getElementById('comments-section');
    if (commentsSection) {
      commentsSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <div className="like-dislike">
        <button
          className="like-dislike__view_comments_button"
          onClick={viewComments}
          aria-label="View Comments"
        >
          View Comments
        </button>

        <div className="like-dislike__group">
          <button
            className={`like-dislike__button ${vote === 'like' ? 'like-dislike__button--liked' : ''}`}
            onClick={handleLike}
            aria-label="Like"
          >
            {vote === 'like' ? <AiFillLike /> : <AiOutlineLike />}
            <span className="like-dislike__count">{(likeCount ?? 0).toLocaleString()}</span>
          </button>
        </div>

        <div className="like-dislike__group">
          <button
            className={`like-dislike__button ${vote === 'dislike' ? 'like-dislike__button--disliked' : ''}`}
            onClick={handleDislike}
            aria-label="Dislike"
          >
            {vote === 'dislike' ? <AiFillDislike /> : <AiOutlineDislike />}
            <span className="like-dislike__count">{(dislikeCount ?? 0).toLocaleString()}</span>
          </button>
        </div>
      </div>

      <AuthRequiredModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        onLogin={() => {
          setAuthModalOpen(false);
          navigate('/auth/signup');
        }}
        title="This feature is for logged-in users"
        description="Create a free account to like or dislike this bill."
        confirmLabel="Sign up"
        dismissLabel="Keep browsing"
        footnote=""
      />
    </>
  );
};

export default LikeDislikeButtons;
